import React, { useEffect, useState } from "react";
import { UserPlus, Loader2, Trash2, Edit2, Check, X, FileSpreadsheet, RefreshCw } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { inviteMember, listMembers, updateMember, deleteMember, bulkUploadMembers } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { MEMBER } from "@/constants/testIds";
import BulkUploadModal from "@/components/BulkUploadModal";

const ROLES = [
  { id: "admin", label: "Admin" },
  { id: "treasurer", label: "Treasurer" },
  { id: "board", label: "Board" },
  { id: "volunteer", label: "Volunteer" },
];

const generateTempPassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const nums = "23456789";
  const syms = "!@#$%^&*";
  const all = upper + lower + nums + syms;

  let p = "";
  p += upper[Math.floor(Math.random() * upper.length)];
  p += lower[Math.floor(Math.random() * lower.length)];
  p += nums[Math.floor(Math.random() * nums.length)];
  p += syms[Math.floor(Math.random() * syms.length)];
  for (let i = 4; i < 12; i++) {
    p += all[Math.floor(Math.random() * all.length)];
  }
  return p.split("").sort(() => 0.5 - Math.random()).join("");
};

export default function Members() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("volunteer");
  const [working, setWorking] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Edit member state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("volunteer");
  const [savingEdit, setSavingEdit] = useState(false);

  // Check if current user is an organization admin
  const userRoles = Array.isArray(user?.roles) && user.roles.length > 0
    ? user.roles
    : (user?.role ? [user.role] : []);

  const isOrgAdmin = userRoles.some((r) =>
    ["admin", "organization_admin", "owner", "account_admin", "executive_director", "super_admin"].includes(
      String(r).toLowerCase()
    )
  );

  const load = () => listMembers().then(setMembers).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!isOrgAdmin) {
      toast({ title: "Permission denied", description: "Only organization admins can add members.", variant: "destructive" });
      return;
    }
    setWorking(true);
    try {
      await inviteMember({ email, full_name: name, password, role });
      toast({ title: "Member added" });
      setEmail(""); setName(""); setPassword(""); setRole("volunteer"); setShowForm(false);
      await load();
    } catch (err) {
      toast({ title: "Could not add member", description: err?.response?.data?.detail || err?.message, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const startEdit = (m) => {
    const currentRole = m.role || (Array.isArray(m.roles) ? m.roles[0] : "volunteer");
    setEditingId(m.id);
    setEditName(m.full_name || "");
    setEditEmail(m.email || "");
    setEditRole(currentRole);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditEmail("");
    setEditRole("volunteer");
  };

  const handleSaveEdit = async (id) => {
    if (!isOrgAdmin) {
      toast({ title: "Permission denied", description: "Only organization admins can edit members.", variant: "destructive" });
      return;
    }
    if (!editEmail.trim()) {
      toast({ title: "Email required", description: "Member email cannot be empty.", variant: "destructive" });
      return;
    }
    setSavingEdit(true);
    try {
      await updateMember(id, { full_name: editName, email: editEmail, role: editRole });
      toast({ title: "Member updated" });
      cancelEdit();
      await load();
    } catch (err) {
      toast({ title: "Could not update member", description: err?.response?.data?.detail || err?.message, variant: "destructive" });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isOrgAdmin) {
      toast({ title: "Permission denied", description: "Only organization admins can delete members.", variant: "destructive" });
      return;
    }
    if (id === user?.id) {
      toast({ title: "Action not allowed", description: "You cannot delete your own admin account.", variant: "destructive" });
      return;
    }
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    setDeletingId(id);
    try {
      await deleteMember(id);
      toast({ title: "Member removed" });
      await load();
    } catch (err) {
      toast({ title: "Could not remove member", description: err?.response?.data?.detail || err?.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageHeader title="Members" subtitle="Your stewardship circle.">
        {isOrgAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card text-foreground px-3.5 py-2 text-sm font-medium hover:bg-accent transition-colors"
            >
              <FileSpreadsheet size={14} className="text-primary" /> Bulk upload
            </button>
            <button
              data-testid={MEMBER.inviteButton}
              onClick={() => {
                setShowForm((v) => {
                  const next = !v;
                  if (next && !password) {
                    setPassword(generateTempPassword());
                  }
                  return next;
                });
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <UserPlus size={14} /> {showForm ? "Cancel" : "Add member"}
            </button>
          </div>
        )}
      </PageHeader>
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        entityType="members"
        onUpload={bulkUploadMembers}
        onSuccess={load}
      />
      <PageBody>
        {isOrgAdmin && showForm && (
          <form onSubmit={submit} className="border border-border bg-card rounded-md p-6 mb-6 grid sm:grid-cols-12 gap-4">
            <label className="sm:col-span-4">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</span>
              <input data-testid={MEMBER.emailInput} required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="td-input mt-1.5" />
            </label>
            <label className="sm:col-span-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Full name</span>
              <input data-testid={MEMBER.nameInput} required value={name} onChange={(e) => setName(e.target.value)} className="td-input mt-1.5" />
            </label>
            <label className="sm:col-span-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Temp password</span>
                <button
                  type="button"
                  onClick={() => setPassword(generateTempPassword())}
                  className="text-[11px] text-primary hover:underline flex items-center gap-1 font-normal"
                >
                  <RefreshCw size={11} /> Auto-generate
                </button>
              </div>
              <input data-testid={MEMBER.passwordInput} required minLength={8} type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="td-input mt-1.5 mono" placeholder="Auto-generated password" />
            </label>
            <label className="sm:col-span-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Role</span>
              <select data-testid={MEMBER.roleSelect} value={role} onChange={(e) => setRole(e.target.value)} className="td-input mt-1.5">
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <div className="sm:col-span-12 flex justify-end">
              <button data-testid={MEMBER.submit} disabled={working} className="rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60 inline-flex items-center gap-1.5">
                {working ? <Loader2 size={14} className="animate-spin" /> : null} Add member
              </button>
            </div>
          </form>
        )}

        <div className="border border-border bg-card rounded-md overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-accent/40 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            <div className="col-span-4">Name</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Joined</div>
            <div className="col-span-2 text-right">Role & Actions</div>
          </div>
          <div className="divide-y divide-border">
            {members.map((m) => {
              const currentRole = m.role || (Array.isArray(m.roles) ? m.roles[0] : "volunteer");
              const roleObj = ROLES.find((r) => r.id === currentRole);
              const roleDisplay = roleObj ? roleObj.label : currentRole;
              const isEditingThisRow = editingId === m.id;

              return (
                <div key={m.id} className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-4 truncate">
                    {isEditingThisRow ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="td-input py-1 text-xs"
                        placeholder="Full Name"
                      />
                    ) : (
                      <>
                        <div className="text-sm font-medium truncate">{m.full_name || "—"}</div>
                        {m.google_linked && <div className="text-[11px] text-muted-foreground mt-0.5">Google linked</div>}
                      </>
                    )}
                  </div>
                  <div className="col-span-4 text-sm text-muted-foreground truncate">
                    {isEditingThisRow ? (
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className="td-input py-1 text-xs"
                        placeholder="Member Email"
                      />
                    ) : (
                      m.email
                    )}
                  </div>
                  <div className="col-span-2 text-xs mono text-muted-foreground">
                    {(() => {
                      const val = m.created_at || m.joined_at;
                      if (!val) return "—";
                      if (typeof val === "string") return val.slice(0, 10);
                      try {
                        return new Date(val).toISOString().slice(0, 10);
                      } catch {
                        return "—";
                      }
                    })()}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    {isOrgAdmin ? (
                      isEditingThisRow ? (
                        <>
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                            className="td-input py-1 text-xs uppercase tracking-wider max-w-[95px]"
                          >
                            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                          <button
                            type="button"
                            data-testid={MEMBER.saveButton ? MEMBER.saveButton(m.id) : `member-save-${m.id}`}
                            onClick={() => handleSaveEdit(m.id)}
                            disabled={savingEdit}
                            className="p-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                            title="Save changes"
                          >
                            {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            type="button"
                            data-testid={MEMBER.cancelButton ? MEMBER.cancelButton(m.id) : `member-cancel-${m.id}`}
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            className="p-1.5 rounded text-muted-foreground hover:bg-accent transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wider bg-muted text-muted-foreground border border-border">
                            {roleDisplay}
                          </span>
                          <button
                            type="button"
                            data-testid={MEMBER.editButton ? MEMBER.editButton(m.id) : `member-edit-${m.id}`}
                            onClick={() => startEdit(m)}
                            className="p-1.5 rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                            title="Edit member"
                          >
                            <Edit2 size={14} />
                          </button>
                          {m.id !== user?.id && (
                            <button
                              type="button"
                              data-testid={MEMBER.deleteButton ? MEMBER.deleteButton(m.id) : `member-delete-${m.id}`}
                              onClick={() => handleDelete(m.id)}
                              disabled={deletingId === m.id}
                              className="p-1.5 rounded text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                              title="Delete member"
                            >
                              {deletingId === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            </button>
                          )}
                        </>
                      )
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium uppercase tracking-wider bg-muted text-muted-foreground border border-border">
                        {roleDisplay}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {members.length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                No members found.
              </div>
            )}
          </div>
        </div>
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}
